import { Injectable } from '@angular/core';

import { NO_VALUE } from '@pipes';
import {
  DevicesStore,
  RemoteServiceDiagnosisEntry,
  RemoteServicePriority,
  RemoteServiceStore,
} from '@store';
import { PillType } from '../../../shared/components/pill/pill.model';

import { SUFFIX } from '../../../shared/common/units';
import { RemoteServiceUnitType } from '../../../operational-data-common/services/units-adapter/units.model';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RemoteServiceService {
  constructor(private remoteServiceStore: RemoteServiceStore, private deviceStore: DevicesStore) {}

  getItemUnit(unit: string): string {
    switch (unit) {
      case RemoteServiceUnitType.Percentage:
      case RemoteServiceUnitType.Percent:
        return ` ${SUFFIX.percentage}`;
      case RemoteServiceUnitType.Celcius:
      case RemoteServiceUnitType.Celsius:
        return ` ${SUFFIX.celsius}`;
      case RemoteServiceUnitType.DecibelMilliwat:
        return ` ${SUFFIX.decibelMilliWatt}`;
      case RemoteServiceUnitType.Fahrenheit:
        return ` ${SUFFIX.fahrenheit}`;
      case RemoteServiceUnitType.Hour:
        return ` ${SUFFIX.hour}`;
      case RemoteServiceUnitType.Kelvin:
        return ` ${SUFFIX.kelvin}`;
      case RemoteServiceUnitType.KiloWattHours:
        return ` ${SUFFIX.kilowattHour}`;
      case RemoteServiceUnitType.LiterPerHour:
        return ` ${SUFFIX.litersPerHour}`;
      case RemoteServiceUnitType.Minute:
        return ` ${SUFFIX.minute}`;
      case RemoteServiceUnitType.Number:
      case RemoteServiceUnitType.StartsPerHour:
        return '';
      case RemoteServiceUnitType.PercentageMinutePerKelvin:
        return ` ${SUFFIX.percentageMinutePerKelvin}`;
      case RemoteServiceUnitType.Bar:
        return ` ${SUFFIX.bar}`;
      case RemoteServiceUnitType.Watt:
        return ` ${SUFFIX.watt}`;
      case RemoteServiceUnitType.Ratio:
      case RemoteServiceUnitType.Boolean:
      case RemoteServiceUnitType.NoUnit:
        return '';
      default:
        return ` ${unit}`;
    }
  }

  getDescriptionKeyByRiskValue(diagnosisEntry: RemoteServiceDiagnosisEntry): string {
    let key: string;

    switch (diagnosisEntry.diagnosis_anomaly) {
      case '-1':
        key = '+REMOTE_SERVICE.TABLE.RECOMMENDATION_ROOT_CAUSE';
        break;
      case '0':
        key = '+REMOTE_SERVICE.TABLE.RECOMMENDATION_NO_RISK';
        break;
      case '1':
        key = `+REMOTE_SERVICE.DIAGNOSIS_PRIORITY_${diagnosisEntry.diagnosis_priority.toUpperCase()}`;
        break;
      default:
        key = NO_VALUE;
        break;
    }

    return key;
  }

  getDashboardDescriptionByRiskValue(riskValue: number): string {
    let key: string;

    switch (riskValue) {
      case 0:
        key = '+REMOTE_SERVICE.RECOMMENDATION_DASHBOARD_NO_RISK';
        break;
      case 1:
        key = '+REMOTE_SERVICE.RECOMMENDATION_DASHBOARD_LOW_RISK';
        break;
      case 2:
        key = '+REMOTE_SERVICE.RECOMMENDATION_DASHBOARD_MEDIUM_RISK';
        break;
      case 4:
        key = '+REMOTE_SERVICE.RECOMMENDATION_DASHBOARD_HIGH_RISK';
        break;
      default:
        key = NO_VALUE;
        break;
    }

    return key;
  }

  getDiagnosisStatusClass(diagnosisEntry: RemoteServiceDiagnosisEntry): { [key: string]: boolean } {
    if (diagnosisEntry.diagnosis_anomaly === '1') {
      return {
        'status-anomaly-information':
          diagnosisEntry.diagnosis_priority === RemoteServicePriority.Information,
        'status-anomaly-warning':
          diagnosisEntry.diagnosis_priority === RemoteServicePriority.Warning,
        'status-anomaly-critical':
          diagnosisEntry.diagnosis_priority === RemoteServicePriority.Critical,
      };
    } else if (diagnosisEntry.diagnosis_anomaly === '0') {
      return {
        'status-no-risk': true,
      };
    } else if (diagnosisEntry.diagnosis_anomaly === '-1') {
      return {
        'status-root-cause': true,
      };
    }
  }

  getDiagnosisStatusPillType(diagnosisEntry: RemoteServiceDiagnosisEntry): PillType {
    if (diagnosisEntry.diagnosis_anomaly === '1') {
      if (diagnosisEntry.diagnosis_priority) {
        if (diagnosisEntry.diagnosis_priority === RemoteServicePriority.Information) {
          return PillType.info;
        } else {
          return PillType.medium;
        }
      }

      return PillType.medium;
    } else if (diagnosisEntry.diagnosis_anomaly === '0') {
      return PillType.successInverted;
    } else if (diagnosisEntry.diagnosis_anomaly === '-1') {
      return PillType.secondaryInverted;
    }
  }

  getRiskStatusPillTypeByRiskValue(riskValue: string | number): PillType {
    if (typeof riskValue === 'number') {
      riskValue = riskValue.toString();
    }

    switch (riskValue) {
      case '0':
        return PillType.successInverted;
      case '1':
      case '2':
      case '4':
        return PillType.errorInverted;
    }
  }

  getRemoteServicePrefix(key: string): string {
    if (key) {
      return `+REMOTE_SERVICE.${key.toUpperCase()}`;
    }

    return key;
  }

  getIsE3HeatpumpDevice(): Observable<boolean> {
    return this.remoteServiceStore.selectedDevice$.pipe(
      concatMap((device) => this.deviceStore.isE3HeatpumpDevice(device))
    );
  }
}
